import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { ProcessEntity } from '../../entities/process.js';

export const ProcessDashboard = stateful(() => {
  // Use useState for the selected conversation and filters
  const [environmentFilter, setEnvironmentFilter] = useState<ConversationEnvironment | 'all'>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [odooModelsFilter, setOdooModelsFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filteredState, setFilteredState] = useState<'all' | 'yes' | 'no'>('all');

  // Function to clear all filters
  const clearFilters = () => {
    setEnvironmentFilter('all');
    setSearchText('');
    setOdooModelsFilter('');
    setStartDate('');
    setEndDate('');
    setFilteredState('all');
  };

  const conversationDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    return [
      buildFullOptions<typeof ProcessEntity>({
        class: ProcessEntity,
        customColumns: [
          {
            fieldName: '_id',
            columnName: 'Process ID',
            columnWidth: '140px',
            renderItem: value => html` ${value._id || 'N/A'} `,
          },
          {
            fieldName: 'processStatus',
            columnName: 'Process Status',
            renderItem: value => html` ${value.processStatus || 'N/A'} `,
          },
          {
            fieldName: ' processType',
            columnName: 'Process Type',
            renderItem: value => html` ${value.processStatus || 'N/A'} `,
          },
          {
            fieldName: 'timestamp',
            columnName: 'Created At',
            columnWidth: '175px',
            renderItem: value => format(value.timestamp ?? new Date(), 'Pp'),
          },
        ],
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['_id', 'processType', 'processStatus'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [environmentFilter, searchText, odooModelsFilter, startDate, endDate, filteredState]);

  return html`
    <div class="flex gap-4 h-full">
      <div class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto w-full">
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Process Overview</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Filtered Status -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Filtered</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${filteredState}
                        @change=${(e: InputEvent) =>
                          setFilteredState((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    <!-- Filter Actions -->
                    <div class="col-start-4 flex justify-end mt-4">
                      <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([conversationDataviewOptions], () =>
              DataTable({
                options: conversationDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  `;
});
